import React, { useRef, useState } from "react";
import {
  Alert,
  Autocomplete,
  Avatar,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { AccountBalance, Add, Delete, Edit, Image as ImageIcon, Upload } from "@mui/icons-material";
import ContactInfoForm, { ContactInfoData } from "./ContactInfoForm";
import {
  useAdminAccounts,
  useAdminBusinesses,
  useAccountLogo,
} from "../../../stores/Admin/locations/locations.queries";
import {
  useCreateAccount,
  useDeleteAccount,
  useUpdateAccount,
  useUploadAccountLogo,
} from "../../../stores/Admin/locations/locations.mutations";
import type {
  UpdateAccountDto,
  CreateAccountDto,
} from "../../../stores/Admin/locations/locations.mutations";
import { TherapyType } from "../../../api-client";

interface AccountFormData {
  id: string;
  name: string;
  abbreviation: string;
  fullName: string;
  ik: string;
  bundesland: string;
  district: string;
  creditorId: string;
  taxId: string;
  therapyType: string;
  businessId: string;
  isCompanyGroup: boolean;
  contactInfo: ContactInfoData;
}

const emptyForm: AccountFormData = {
  id: "",
  name: "",
  abbreviation: "",
  fullName: "",
  ik: "",
  bundesland: "",
  district: "",
  creditorId: "",
  taxId: "",
  therapyType: TherapyType.Physiotherapie,
  businessId: "",
  isCompanyGroup: true,
  contactInfo: {},
};

const AccountSection: React.FC = () => {
  const { data: accounts = [], isLoading, isError } = useAdminAccounts();
  const { data: businesses = [] } = useAdminBusinesses();
  const createMutation = useCreateAccount();
  const updateMutation = useUpdateAccount();
  const deleteMutation = useDeleteAccount();
  const uploadLogoMutation = useUploadAccountLogo();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState<AccountFormData>(emptyForm);

  const isEditMode = editId !== null;

  const logoInputRef = useRef<HTMLInputElement>(null);
  const { data: logo, isLoading: isLogoLoading } = useAccountLogo(editId, isEditMode && dialogOpen);

  const handleLogoSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !editId) return;
    await uploadLogoMutation.mutateAsync({ id: editId, file });
  };

  const openCreate = () => {
    setEditId(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (row: any) => {
    setEditId(row.id);
    setForm({
      id: row.id,
      name: row.name || "",
      abbreviation: row.abbreviation || "",
      fullName: row.fullName || "",
      ik: row.ik || "",
      bundesland: row.bundesland || "",
      district: row.district || "",
      creditorId: row.creditorId || "",
      taxId: row.taxId || "",
      therapyType: row.therapyType || TherapyType.Physiotherapie,
      businessId: row.businessId || "",
      isCompanyGroup: row.isCompanyGroup ?? true,
      contactInfo: row.contactInfo || {},
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      const payload: UpdateAccountDto = {
        name: form.name,
        abbreviation: form.abbreviation || undefined,
        fullName: form.fullName || undefined,
        ik: form.ik || undefined,
        bundesland: form.bundesland || undefined,
        district: form.district || undefined,
        creditorId: form.creditorId || undefined,
        taxId: form.taxId || undefined,
        therapyType: (form.therapyType as TherapyType) || undefined,
        businessId: form.businessId || undefined,
        isCompanyGroup: form.isCompanyGroup,
        contactInfo: form.contactInfo,
      };
      if (isEditMode) {
        await updateMutation.mutateAsync({ id: editId!, dto: payload });
      } else {
        await createMutation.mutateAsync({
          ...payload,
          id: form.id,
          name: form.name,
        } as CreateAccountDto);
      }
      setDialogOpen(false);
    } catch {
      // error handled by mutation onError
    }
  };

  const handleDelete = (row: any) => {
    if (!window.confirm(`Mandant "${row.name}" wirklich löschen?`)) return;
    deleteMutation.mutate(row.id);
  };

  const cellSx = { fontWeight: "bold", backgroundColor: "#f5f5f5" };

  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <AccountBalance sx={{ fontSize: 28 }} />
          <Box>
            <Typography variant="h6">Mandanten</Typography>
            <Typography variant="body2" color="text.secondary">
              {accounts.length} Einträge
            </Typography>
          </Box>
        </Box>
        <Button variant="contained" size="small" startIcon={<Add />} onClick={openCreate}>
          Neuer Mandant
        </Button>
      </Box>

      {/* Table */}
      <Paper>
        <TableContainer sx={{ maxHeight: "calc(100vh - 380px)" }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={cellSx}>Name</TableCell>
                <TableCell sx={cellSx}>Kürzel</TableCell>
                <TableCell sx={cellSx}>Typ</TableCell>
                <TableCell sx={cellSx}>IK</TableCell>
                <TableCell sx={cellSx}>Bundesland</TableCell>
                <TableCell sx={cellSx}>Unternehmen</TableCell>
                <TableCell sx={cellSx}>Gläubiger-ID</TableCell>
                <TableCell sx={cellSx} align="center">
                  Aktionen
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : isError ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <Alert severity="error">Mandanten konnten nicht geladen werden</Alert>
                  </TableCell>
                </TableRow>
              ) : accounts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">Keine Mandanten vorhanden</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                accounts.map((row: any) => (
                  <TableRow
                    key={row.id}
                    hover
                    sx={{ cursor: "pointer" }}
                    onClick={() => openEdit(row)}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {row.name}
                      </Typography>
                      {row.fullName && row.fullName !== row.name && (
                        <Typography variant="caption" color="text.secondary">
                          {row.fullName}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{row.abbreviation || "-"}</TableCell>{" "}
                    <TableCell>
                      <Chip
                        label={row.therapyType === TherapyType.Ergotherapie ? "Ergo" : "Physio"}
                        size="small"
                        color={row.therapyType === TherapyType.Ergotherapie ? "secondary" : "info"}
                        variant="outlined"
                      />
                    </TableCell>{" "}
                    <TableCell sx={{ fontSize: "0.8rem", fontFamily: "monospace" }}>
                      {row.ik || "-"}
                    </TableCell>
                    <TableCell>{row.bundesland || "-"}</TableCell>
                    <TableCell>
                      {row.businessName ? (
                        <Chip label={row.businessName} size="small" variant="outlined" />
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          —
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ fontSize: "0.8rem" }}>{row.creditorId || "-"}</TableCell>
                    <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                      <Tooltip title="Bearbeiten">
                        <IconButton size="small" onClick={() => openEdit(row)}>
                          <Edit fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Löschen">
                        <IconButton size="small" onClick={() => handleDelete(row)} color="error">
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ bgcolor: "#e8f5e9" }}>
          {isEditMode ? `Mandant bearbeiten: ${editId}` : "Neuen Mandant erstellen"}
        </DialogTitle>
        <DialogContent sx={{ pt: "16px !important" }}>
          <Grid container spacing={2}>
            {!isEditMode && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  label="ID"
                  required
                  value={form.id}
                  onChange={(e) => setForm({ ...form, id: e.target.value })}
                />
              </Grid>
            )}
            <Grid item xs={8}>
              <TextField
                fullWidth
                size="small"
                label="Name"
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth
                size="small"
                label="Kürzel"
                value={form.abbreviation}
                onChange={(e) => setForm({ ...form, abbreviation: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Voller Name"
                value={form.fullName}
                onChange={(e) => setForm({ ...form, fullName: e.target.value })}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth
                size="small"
                label="IK-Nummer"
                value={form.ik}
                onChange={(e) => setForm({ ...form, ik: e.target.value })}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth
                size="small"
                label="Bundesland"
                value={form.bundesland}
                onChange={(e) => setForm({ ...form, bundesland: e.target.value })}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth
                size="small"
                label="Bezirk"
                value={form.district}
                onChange={(e) => setForm({ ...form, district: e.target.value })}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                size="small"
                label="Gläubiger-ID"
                value={form.creditorId}
                onChange={(e) => setForm({ ...form, creditorId: e.target.value })}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                size="small"
                label="Steuernummer"
                value={form.taxId}
                onChange={(e) => setForm({ ...form, taxId: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel>Therapie-Typ</InputLabel>
                <Select
                  value={form.therapyType}
                  label="Therapie-Typ"
                  onChange={(e) => setForm({ ...form, therapyType: e.target.value })}
                >
                  <MenuItem value={TherapyType.Physiotherapie}>Physiotherapie</MenuItem>
                  <MenuItem value={TherapyType.Ergotherapie}>Ergotherapie</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <Autocomplete
                size="small"
                options={businesses}
                getOptionLabel={(o: any) => o.name || o.id || ""}
                value={businesses.find((b: any) => b.id === form.businessId) || null}
                onChange={(_, v: any) => setForm({ ...form, businessId: v?.id || "" })}
                renderInput={(params) => <TextField {...params} label="Unternehmen" />}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={form.isCompanyGroup}
                    onChange={(e) =>
                      setForm({ ...form, isCompanyGroup: e.target.checked })
                    }
                  />
                }
                label="Unternehmensgruppe"
              />
            </Grid>
          </Grid>

          {isEditMode && (
            <>
              <Typography variant="subtitle1" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>
                Logo
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Avatar
                  variant="rounded"
                  src={(logo?.accessUrl as string | undefined) || undefined}
                  sx={{ width: 80, height: 80, bgcolor: "#f5f5f5" }}
                >
                  {isLogoLoading ? <CircularProgress size={24} /> : <ImageIcon color="disabled" />}
                </Avatar>
                <input
                  ref={logoInputRef}
                  type="file"
                  accept="image/*"
                  style={{ display: "none" }}
                  onChange={handleLogoSelect}
                />
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<Upload />}
                  onClick={() => logoInputRef.current?.click()}
                  disabled={uploadLogoMutation.isPending}
                  color="primary"
                  sx={{ color: "black" }}
                >
                  {uploadLogoMutation.isPending
                    ? "Wird hochgeladen…"
                    : logo?.accessUrl
                      ? "Logo ersetzen"
                      : "Logo hochladen"}
                </Button>
              </Box>
              {!isLogoLoading && !logo?.accessUrl && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  {`Mandant „${form.name || editId}" hat kein Logo. Sie können hier eines hochladen.`}
                </Alert>
              )}
            </>
          )}

          <Typography variant="subtitle1" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>
            Kontaktinformationen
          </Typography>
          <ContactInfoForm
            value={form.contactInfo}
            onChange={(ci) => setForm({ ...form, contactInfo: ci })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Abbrechen</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!form.name || (!isEditMode && !form.id)}
          >
            Speichern
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AccountSection;
